import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/dist/ScrollToPlugin";
import { useEffect, useState } from "react";
import { usStreet } from "smartystreets-javascript-sdk";

import Modal from "rivals/components/shared/Modal";
import Spinner from "rivals/components/shared/Spinner";
import {
  resetAddressValidationFields,
  validateAddressThroughTaxEstimation
} from "rivals/redux/rails/actions";
import {
  selectAddressValidationError,
  selectAddressValidationSuccess
} from "rivals/redux/rails/selectors";
import useSmartyStreets from "rivals/shared/hooks/useSmartyStreets";
import { useAppDispatch, useAppSelector } from "rivals/shared/utils/hooks";
import {
  buildAddressLookup,
  extractAddressFromCandidate,
  getAddressBestCandidate,
  validSmartyCandidate
} from "rivals/shared/utils/smartyStreets";
import styles from "rivals/styles/exports.module.scss";
import ModalBody from "./ModalBody";
import strings from "./strings";
import type { Props } from "./types";

const { correctableState, errorState, modalTitle } = strings;

/**
 * Address states are:
  * Completely Invalid: OBI returned invalid address error, smarty didn't have a good candidate to fix it,
    or smarty had a good candidate but OBI rejected it
  * Invalid but correctable: OBI returned invalid address error, smarty had a good candidate and OBI approved it
*/

const confirmButtonText = (
  isValidAddress: boolean,
  loadingData: boolean
): string => {
  if (loadingData) {
    return "";
  }

  if (isValidAddress) {
    return correctableState.confirmText;
  }

  return errorState.confirmText;
};

// Cancel button disappears when address is completely invalid, user has only 1 option
// which is to close the modal and edit it manually
const cancelButtonText = (
  isValidAddress: boolean,
  loadingData: boolean
): string => {
  if (loadingData || !isValidAddress) {
    return "";
  }

  return correctableState.cancelText;
};

const AddressVerificationModal = ({
  addressContainerRef,
  changeVisibility,
  formAddress,
  siteId,
  smartyAuthToken,
  updateAddress,
  visible
}: Props): JSX.Element => {
  const [isValidAddress, setIsValidAddress] = useState(false);
  const [addressCandidate, setAddressCandidate] = useState<
    usStreet.Candidate
  >();
  const [loadingData, setLoadingData] = useState(false);

  const dispatch = useAppDispatch();
  const addressValidationError = useAppSelector(selectAddressValidationError);
  const addressValidationSuccess = useAppSelector(
    selectAddressValidationSuccess
  );

  const { usStreetClient } = useSmartyStreets(smartyAuthToken);

  const estimateTaxAgainstNewAddress = (
    candidate: usStreet.Candidate
  ): void => {
    if (siteId) {
      const address = extractAddressFromCandidate(candidate);
      // It doesn't matter whether it's a subscription purchase or a subscription gift
      // both run the same method (estimate_price_with_tax) on subscription model
      dispatch(validateAddressThroughTaxEstimation(address, siteId));
    }
  };

  useEffect(() => {
    gsap.registerPlugin(ScrollToPlugin);

    // show spinner right when the modal is open
    setLoadingData(true);

    // calls smarty street to try fixing the form address
    const lookup = buildAddressLookup(formAddress);
    if (usStreetClient.current) {
      usStreetClient.current
        .send(lookup)
        .then(({ lookups }) => getAddressBestCandidate(lookups))
        .then(bestCandidate => {
          // calls estimate tax to verify suggested address with OBI
          const isValidSmartyCandidate = validSmartyCandidate(bestCandidate);
          if (isValidSmartyCandidate) {
            estimateTaxAgainstNewAddress(bestCandidate);
            setAddressCandidate(bestCandidate);
          } else {
            // OPTIONAL since isValidAddress is false by default
            setIsValidAddress(false);
            setLoadingData(false);
          }
        })
        .catch(() => {
          // smarty should not fail even with the most ridiculous addresses
          // but doesn't harm to do something if it fails
          setIsValidAddress(false);
          setLoadingData(false);
        });
    }
  }, []);

  useEffect(() => {
    if (addressValidationSuccess || !!addressValidationError) {
      // stops the spinner and shows the data when address validation through getTax has returned something
      setLoadingData(false);
    }

    if (addressValidationSuccess) {
      // smarty candidate is only considered valid when OBI approves it
      setIsValidAddress(true);
    }

    if (addressValidationError) {
      // OPTIONAL since isValidAddress is false by default
      setIsValidAddress(false);
    }
  }, [addressValidationSuccess, addressValidationError]);

  const resetOnClose = (): void => {
    dispatch(resetAddressValidationFields());
  };

  const closeAndScrollToAddressSection = (): void => {
    changeVisibility(false);

    // offsetY value is to avoid header from covering the first `header-height-total` pixels
    if (addressContainerRef.current) {
      gsap.to(window, {
        duration: 0.25,
        scrollTo: {
          offsetY: parseInt(styles["header-height-total"]),
          y: addressContainerRef.current
        }
      });
    }
  };

  const onCancel = (): void => {
    // close the modal and scroll to street 1 field
    closeAndScrollToAddressSection();
    resetOnClose();
  };

  const onConfirm = (): void => {
    if (isValidAddress) {
      // change the address and close the modal
      const address = extractAddressFromCandidate(addressCandidate);
      updateAddress({
        city: { error: "", value: address.city || "" },
        state: { error: "", value: address.state || "" },
        street1: { error: "", value: address.street1 || "" },
        street2: { error: "", value: address.street2 || "" },
        zip: { error: "", value: address.zip || "" }
      });
      changeVisibility(false);
      resetOnClose();
      return;
    }

    onCancel();
  };

  return (
    <Modal
      cancelButtonText={cancelButtonText(isValidAddress, loadingData)}
      confirmButtonText={confirmButtonText(isValidAddress, loadingData)}
      handleCancel={onCancel}
      handleConfirm={onConfirm}
      hasCloseIcon
      maskClosable={false}
      open={visible}
      title={modalTitle}
    >
      {loadingData ? (
        <Spinner />
      ) : (
        <ModalBody
          addressCandidate={addressCandidate}
          formAddress={formAddress}
          isValidAddress={isValidAddress}
        />
      )}
    </Modal>
  );
};

export default AddressVerificationModal;
